import React from 'react';
import { Box, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const footerLinks = {
    Services: [
      { name: 'Construction', path: '/construction' },
      { name: 'Healthcare', path: '/healthcare' },
      { name: 'Hologram Technology', path: '/hologram' },
      { name: 'CGI Staging', path: '/cgi' },
      { name: 'Leisure', path: '/leisure' },
      { name: 'Drone Surveys', path: '/drone-surveys' },
      { name: 'Timelapse Cameras', path: '/timelapse' },
    ],
    Company: [
      { name: 'About Us', path: '/about' },
      { name: 'Insights', path: '/blog' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Contact', path: '/contact' },
    ],
  };

  const socialLinks = [
    {
      icon: Linkedin,
      link: 'https://www.linkedin.com/company/digital-scanning-group',
      color: 'hover:text-blue-600',
      label: 'Follow Digital Scanning Group on LinkedIn',
    },
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6 group relative" aria-label="Digital Scanning Group home page">
              {/* Hologram glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>

              <img
                src="https://customer-assets.emergentagent.com/job_site-refresh-91/artifacts/e8l21ohj_DSG%20logo%20maximum.png"
                alt="Digital Scanning Group logo - 3D scanning and digital twin specialists based in London"
                className="h-24 w-auto relative z-10 transition-all duration-300 group-hover:scale-105"
                style={{
                  filter: 'drop-shadow(0 0 15px rgba(34, 211, 238, 0.4))',
                  imageRendering: 'crisp-edges'
                }}
              />
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Shaping the future with advanced Matterport 3D scanning, point cloud surveys, Revit BIM modelling and digital twin technologies. Serving construction, healthcare, heritage and commercial sectors across the UK and Europe.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`p-2 bg-gray-800 rounded-lg ${social.color} transition-all duration-300 hover:scale-110`}
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-lg font-bold mb-6">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      aria-label={`Navigate to ${link.name} page`}
                      className="text-gray-400 hover:text-cyan-400 transition-colors inline-block hover:translate-x-1 duration-300"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-6">Contact</h3>
            <ul className="space-y-4 text-gray-400">
              <li>
                <a
                  href="mailto:lee@digitalscanninggroup.com"
                  className="hover:text-cyan-400 transition-colors flex items-center gap-3"
                  aria-label="Email Digital Scanning Group at lee@digitalscanninggroup.com"
                >
                  <Mail className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                  lee@digitalscanninggroup.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+447577705477"
                  className="hover:text-cyan-400 transition-colors flex items-center gap-3"
                  aria-label="Call Digital Scanning Group on +44 7577 705477"
                >
                  <Phone className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                  +44 7577 705477
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <address className="not-italic">
                  167-169 Great Portland Street,<br />
                  London W1W 5PF,<br />
                  United Kingdom
                </address>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Digital Scanning Group. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <a
                href="/privacy"
                className="text-gray-400 hover:text-cyan-400 transition-colors"
                aria-label="Read our privacy policy"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-gray-400 hover:text-cyan-400 transition-colors"
                aria-label="Read our terms of service"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
